import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import TodayIcon from "@mui/icons-material/Today";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import StorefrontIcon from "@mui/icons-material/Storefront";
import {
  Ingredient,
  IngredientStore,
  MealPlan,
  MealPlanEntry,
  MealPlanView,
  MealTemplate,
  MealType,
  Supply,
} from "../../lib/types";
import {
  ingredientAPI,
  mealPlanAPI,
  mealPlanEntryAPI,
  mealTemplateAPI,
  supplyAPI,
} from "../../services/api";

const mealTypes: MealType[] = ["breakfast", "lunch", "dinner", "snack"];
const stores: IngredientStore[] = [
  "Costco",
  "Walmart",
  "Target",
  "Bi-Mart",
  "Grocery Outlet",
];

const compactButtonSx = {
  py: 0,
  px: 1.5,
  minHeight: 30,
  height: 30,
  lineHeight: 1.1,
  "& .MuiButton-startIcon": {
    mr: 0.5,
    "& > *:nth-of-type(1)": {
      fontSize: 16,
    },
  },
};

const cardContentSx = {
  position: "relative",
  zIndex: 2,
};

const ledgerItemRowSx = {
  borderRadius: "5px",
  p: 1.25,
  border: 1,
  borderColor: "divider",
  backgroundImage: "none",
  backgroundColor: "background.paper",
  "& .MuiTypography-root": {
    color: "text.primary",
  },
  "& .MuiTypography-body2": {
    color: "text.secondary",
  },
};

const formatDateInput = (value: Date | string) => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const monthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(end),
    title: start.toLocaleString(undefined, {
      month: "long",
      year: "numeric",
    }),
  };
};

const getMealTypeIcon = (mealType: MealType) => {
  switch (mealType) {
    case "breakfast":
      return "🍳";
    case "lunch":
      return "🥪";
    case "dinner":
      return "🍽️";
    case "snack":
      return "🍎";
    default:
      return "🍽️";
  }
};

const formatMealTypeLabel = (mealType: MealType) =>
  mealType.charAt(0).toUpperCase() + mealType.slice(1);

const formatScheduledMealDate = (value: Date) =>
  value.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const GroceryMeals: React.FC = () => {
  const initialRange = useMemo(() => monthRange(), []);
  const [mealTemplates, setMealTemplates] = useState<MealTemplate[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [mealPlans, setMealPlans] = useState<MealPlan[]>([]);
  const [selectedMealType, setSelectedMealType] = useState<MealType | "all">(
    "all"
  );
  const [view, setView] = useState<MealPlanView>("month");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [selectedMealPlanId, setSelectedMealPlanId] = useState<string>("");
  const [openMealModal, setOpenMealModal] = useState(false);
  const [savingMealPlan, setSavingMealPlan] = useState(false);
  const [generatingMeal, setGeneratingMeal] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>("");
  const [generatingSummaryType, setGeneratingSummaryType] =
    useState<MealPlanView | null>(null);
  const [mealDraft, setMealDraft] = useState({
    query: "",
    mealType: "dinner" as MealType,
    scheduledAt: `${initialRange.startDate}T18:00`,
    recurrenceType: "none",
    notes: "",
    dietaryHints: "",
  });

  const loadData = useCallback(async () => {
    console.debug("[GroceryMeals] Loading data", {
      startDate,
      endDate,
      selectedMealType,
    });
    const [templates, ingredientRows, supplyRows, plans] = await Promise.all([
      mealTemplateAPI.getAll(
        selectedMealType === "all" ? undefined : { mealType: selectedMealType }
      ),
      ingredientAPI.getAll(),
      supplyAPI.getAll(),
      mealPlanAPI.getAll({ startDate, endDate }),
    ]);
    setMealTemplates(templates);
    setIngredients(ingredientRows);
    setSupplies(supplyRows);
    setMealPlans(plans);
    setSelectedMealPlanId((current) => current || plans[0]?.id || "");
  }, [endDate, selectedMealType, startDate]);

  useEffect(() => {
    loadData().catch((error) => {
      console.error("[GroceryMeals] Failed to load", error);
      setStatusMessage(error.message || "Failed to load Grocery & Meals.");
    });
  }, [loadData]);

  const filteredMealTemplates = useMemo(() => {
    const query = search.trim().toLowerCase();
    return mealTemplates.filter((mealTemplate) => {
      const typeMatch =
        selectedMealType === "all" ||
        mealTemplate.mealType === selectedMealType;
      if (!typeMatch) return false;
      if (!query) return true;
      return (
        mealTemplate.name.toLowerCase().includes(query) ||
        (mealTemplate.description || "").toLowerCase().includes(query) ||
        mealTemplate.tags.some((tag) => tag.toLowerCase().includes(query)) ||
        mealTemplate.ingredients.some((ingredient) =>
          (ingredient.ingredient?.name || "").toLowerCase().includes(query)
        ) ||
        mealTemplate.supplies.some((supply) =>
          (supply.supply?.name || "").toLowerCase().includes(query)
        )
      );
    });
  }, [mealTemplates, search, selectedMealType]);

  const selectedMealPlan = useMemo(
    () =>
      mealPlans.find((mealPlan) => mealPlan.id === selectedMealPlanId) || null,
    [mealPlans, selectedMealPlanId]
  );

  const visibleEntries = useMemo(() => {
    const entries = selectedMealPlan?.entries || [];
    return [...entries].sort(
      (left, right) => left.scheduledAt.getTime() - right.scheduledAt.getTime()
    );
  }, [selectedMealPlan]);

  const projectedSpend = useMemo(() => {
    return visibleEntries.reduce((sum, entry) => {
      const template = entry.mealTemplate;
      if (!template) return sum;
      return (
        sum +
        template.ingredients.reduce((templateSum, ingredient) => {
          const price = Number(ingredient.ingredient?.defaultPrice || 0);
          return templateSum + price * Number(ingredient.quantity || 1);
        }, 0)
      );
    }, 0);
  }, [visibleEntries]);

  const missingIngredientCount = useMemo(() => {
    return visibleEntries.reduce((sum, entry) => {
      const template = entry.mealTemplate;
      if (!template) return sum;
      return (
        sum +
        template.ingredients.filter(
          (ingredient) => ingredient.ingredient?.defaultPrice == null
        ).length
      );
    }, 0);
  }, [visibleEntries]);

  const aggregatedIngredients = useMemo(() => {
    const aggregation = new Map<
      string,
      { name: string; total: number; store: string; price: number }
    >();
    visibleEntries.forEach((entry) => {
      entry.mealTemplate?.ingredients.forEach((ingredient) => {
        const key =
          ingredient.ingredient?.id ||
          ingredient.ingredient?.name ||
          ingredient.unit;
        const current = aggregation.get(key) || {
          name: ingredient.ingredient?.name || "Unknown ingredient",
          total: 0,
          store: ingredient.ingredient?.defaultStore || "Costco",
          price: Number(ingredient.ingredient?.defaultPrice || 0),
        };
        current.total += Number(ingredient.quantity || 1);
        aggregation.set(key, current);
      });
    });
    return Array.from(aggregation.values()).sort((left, right) =>
      left.name.localeCompare(right.name)
    );
  }, [visibleEntries]);

  const createMealPlan = useCallback(async () => {
    setSavingMealPlan(true);
    try {
      const createdPlan = await mealPlanAPI.create({
        title: `${new Date(startDate).toLocaleString(undefined, {
          month: "long",
          year: "numeric",
        })} Meal Plan`,
        startDate,
        endDate,
      });
      console.debug("[GroceryMeals] Created meal plan", createdPlan.id);
      setSelectedMealPlanId(createdPlan.id);
      setMealPlans((current) => [...current, createdPlan]);
      setStatusMessage("Meal plan created.");
    } catch (error: any) {
      console.error("[GroceryMeals] Failed to create meal plan", error);
      setStatusMessage(error.message || "Failed to create meal plan.");
    } finally {
      setSavingMealPlan(false);
    }
  }, [endDate, startDate]);

  const ensureMealPlanId = useCallback(async (): Promise<string | null> => {
    if (selectedMealPlanId) {
      return selectedMealPlanId;
    }

    setSavingMealPlan(true);
    try {
      const createdPlan = await mealPlanAPI.create({
        title: `${new Date(startDate).toLocaleString(undefined, {
          month: "long",
          year: "numeric",
        })} Meal Plan`,
        startDate,
        endDate,
      });
      console.debug("[GroceryMeals] Auto-created meal plan", createdPlan.id);
      setSelectedMealPlanId(createdPlan.id);
      setMealPlans((current) => [...current, createdPlan]);
      return createdPlan.id;
    } catch (error: any) {
      console.error("[GroceryMeals] Failed to auto-create meal plan", error);
      setStatusMessage(error.message || "Failed to create meal plan.");
      return null;
    } finally {
      setSavingMealPlan(false);
    }
  }, [endDate, selectedMealPlanId, startDate]);

  const handleCreateMeal = useCallback(async () => {
    if (!selectedMealPlanId) {
      setStatusMessage("Create a meal plan first.");
      return;
    }

    try {
      let targetMeal = filteredMealTemplates.find(
        (mealTemplate) =>
          mealTemplate.name.toLowerCase() ===
          mealDraft.query.trim().toLowerCase()
      );

      if (!targetMeal) {
        setGeneratingMeal(true);
        targetMeal = await mealTemplateAPI.generate({
          query: mealDraft.query,
          mealType: mealDraft.mealType,
          dietaryHints: mealDraft.dietaryHints
            .split(",")
            .map((hint) => hint.trim())
            .filter(Boolean),
        });
        console.debug("[GroceryMeals] Generated meal", targetMeal.id);
        setMealTemplates((current) => [targetMeal!, ...current]);
      }

      const createdEntry = await mealPlanEntryAPI.create(selectedMealPlanId, {
        mealTemplateId: targetMeal.id,
        scheduledAt: new Date(mealDraft.scheduledAt).toISOString(),
        recurrenceType:
          mealDraft.recurrenceType as MealPlanEntry["recurrenceType"],
        recurrenceConfig: {},
        notes: mealDraft.notes,
      });
      const appendedEntry: MealPlanEntry = {
        ...createdEntry,
        mealTemplate: targetMeal,
      };

      console.debug("[GroceryMeals] Created meal plan entry", createdEntry.id);
      setMealPlans((current) =>
        current.map((mealPlan) =>
          mealPlan.id === selectedMealPlanId
            ? {
                ...mealPlan,
                entries: [...mealPlan.entries, appendedEntry],
              }
            : mealPlan
        )
      );
      setOpenMealModal(false);
      setStatusMessage("Meal scheduled.");
    } catch (error: any) {
      console.error("[GroceryMeals] Failed to create meal", error);
      setStatusMessage(error.message || "Failed to schedule meal.");
    } finally {
      setGeneratingMeal(false);
    }
  }, [filteredMealTemplates, mealDraft, selectedMealPlanId]);

  const duplicateSelectedPlan = useCallback(
    async (dayShift: number, label: string) => {
      if (!selectedMealPlan) {
        setStatusMessage("Select a meal plan first.");
        return;
      }
      const nextStart = new Date(selectedMealPlan.startDate);
      nextStart.setDate(nextStart.getDate() + dayShift);
      const nextEnd = new Date(selectedMealPlan.endDate);
      nextEnd.setDate(nextEnd.getDate() + dayShift);
      try {
        const duplicatedPlan = await mealPlanAPI.create({
          title: `${selectedMealPlan.title} (${label})`,
          startDate: nextStart.toISOString(),
          endDate: nextEnd.toISOString(),
        });
        let latestPlan = duplicatedPlan;
        for (const entry of selectedMealPlan.entries) {
          const nextScheduledAt = new Date(entry.scheduledAt);
          nextScheduledAt.setDate(nextScheduledAt.getDate() + dayShift);
          const createdEntry = await mealPlanEntryAPI.create(
            duplicatedPlan.id,
            {
              mealTemplateId: entry.mealTemplateId,
              scheduledAt: nextScheduledAt.toISOString(),
              recurrenceType: entry.recurrenceType,
              recurrenceConfig: entry.recurrenceConfig,
              notes: entry.notes,
            }
          );
          const duplicatedEntry: MealPlanEntry = {
            ...createdEntry,
            mealTemplate: entry.mealTemplate,
          };
          latestPlan = {
            ...latestPlan,
            entries: [...(latestPlan.entries || []), duplicatedEntry],
          };
        }
        setMealPlans((current) => [...current, latestPlan]);
        setSelectedMealPlanId(duplicatedPlan.id);
        setStatusMessage(`Meal plan duplicated ${label.toLowerCase()}.`);
      } catch (error: any) {
        console.error("[GroceryMeals] Failed to duplicate meal plan", error);
        setStatusMessage(error.message || "Failed to duplicate meal plan.");
      }
    },
    [selectedMealPlan]
  );

  const addMealTemplateToPlan = useCallback(
    async (mealTemplate: MealTemplate) => {
      const targetMealPlanId = await ensureMealPlanId();
      if (!targetMealPlanId) {
        return;
      }

      try {
        const createdEntry = await mealPlanEntryAPI.create(targetMealPlanId, {
          mealTemplateId: mealTemplate.id,
          scheduledAt: new Date(`${startDate}T18:00:00`).toISOString(),
          recurrenceType: "none",
          recurrenceConfig: {},
          notes: null,
        });

        const appendedEntry: MealPlanEntry = {
          ...createdEntry,
          mealTemplate,
        };

        setMealPlans((current) =>
          current.map((mealPlan) =>
            mealPlan.id === targetMealPlanId
              ? {
                  ...mealPlan,
                  entries: [...mealPlan.entries, appendedEntry],
                }
              : mealPlan
          )
        );
        setStatusMessage(`${mealTemplate.name} added to the calendar.`);
      } catch (error: any) {
        console.error(
          "[GroceryMeals] Failed to add meal template to plan",
          error
        );
        setStatusMessage(error.message || "Failed to add meal to calendar.");
      }
    },
    [ensureMealPlanId, startDate]
  );

  const updateSelectedMealPlan = useCallback(
    async (payload: Record<string, unknown>) => {
      if (!selectedMealPlanId) return;
      try {
        const updatedPlan = await mealPlanAPI.update(
          selectedMealPlanId,
          payload
        );
        setMealPlans((current) =>
          current.map((mealPlan) =>
            mealPlan.id === updatedPlan.id ? updatedPlan : mealPlan
          )
        );
      } catch (error: any) {
        console.error("[GroceryMeals] Failed to update meal plan", error);
        setStatusMessage(error.message || "Failed to update meal plan.");
      }
    },
    [selectedMealPlanId]
  );

  const generateSummary = useCallback(
    async (summaryType: MealPlanView) => {
      if (!selectedMealPlanId) {
        setStatusMessage("Create a meal plan first.");
        return;
      }
      try {
        setGeneratingSummaryType(summaryType);
        const updatedPlan = await mealPlanAPI.generateSummary(
          selectedMealPlanId,
          summaryType
        );
        setMealPlans((current) =>
          current.map((mealPlan) =>
            mealPlan.id === updatedPlan.id ? updatedPlan : mealPlan
          )
        );
        setStatusMessage(`${summaryType} summary generated.`);
      } catch (error: any) {
        console.error("[GroceryMeals] Failed to generate summary", error);
        setStatusMessage(error.message || "Failed to generate summary.");
      } finally {
        setGeneratingSummaryType(null);
      }
    },
    [selectedMealPlanId]
  );

  const activeSummaryField = useMemo(() => {
    if (view === "week") {
      return {
        label: "Week Description",
        field: "weekDescription" as const,
        value: selectedMealPlan?.weekDescription || "",
      };
    }
    if (view === "day") {
      return {
        label: "Day Description",
        field: "dayDescription" as const,
        value: selectedMealPlan?.dayDescription || "",
      };
    }
    return {
      label: "Month Description",
      field: "monthDescription" as const,
      value: selectedMealPlan?.monthDescription || "",
    };
  }, [selectedMealPlan, view]);

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3, borderRadius: "5px" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Grocery & Meals
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Plan meals, keep a live ingredient ledger, and generate new meals
              with AI when your catalog misses.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              variant="contained"
              startIcon={<CalendarMonthIcon />}
              onClick={createMealPlan}
              disabled={savingMealPlan}
              size="small"
              sx={compactButtonSx}
            >
              Create Meal Plan
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => duplicateSelectedPlan(7, "Next Week")}
              disabled={!selectedMealPlan}
              size="small"
              sx={compactButtonSx}
            >
              Duplicate Next Week
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => duplicateSelectedPlan(28, "Next Month")}
              disabled={!selectedMealPlan}
              size="small"
              sx={compactButtonSx}
            >
              Duplicate Next Month
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
        <Paper sx={{ flex: 2, p: 3, borderRadius: "5px" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ md: "center" }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Meal Plan Calendar
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedMealPlan?.title || "No meal plan selected"}
              </Typography>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                mt={1.5}
                alignItems={{ md: "flex-start" }}
              >
                <TextField
                  label={activeSummaryField.label}
                  value={activeSummaryField.value}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setMealPlans((current) =>
                      current.map((mealPlan) =>
                        mealPlan.id === selectedMealPlanId
                          ? {
                              ...mealPlan,
                              [activeSummaryField.field]: nextValue,
                            }
                          : mealPlan
                      )
                    );
                  }}
                  onBlur={(event) =>
                    updateSelectedMealPlan({
                      [activeSummaryField.field]: event.target.value,
                    })
                  }
                  multiline
                  minRows={2}
                  fullWidth
                  sx={{ minWidth: { md: 360 } }}
                />
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => generateSummary(view)}
                  disabled={
                    !selectedMealPlanId || generatingSummaryType === view
                  }
                  size="small"
                  sx={compactButtonSx}
                >
                  AI
                </Button>
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              <IconButton
                color="primary"
                onClick={() => setOpenMealModal(true)}
                size="small"
                sx={{
                  border: 1,
                  borderColor: "primary.main",
                  borderRadius: "5px",
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              <Button
                variant={view === "month" ? "contained" : "outlined"}
                startIcon={<CalendarMonthIcon />}
                onClick={() => setView("month")}
                sx={compactButtonSx}
              >
                Month
              </Button>
              <Button
                variant={view === "week" ? "contained" : "outlined"}
                startIcon={<ViewWeekIcon />}
                onClick={() => setView("week")}
                sx={compactButtonSx}
              >
                Week
              </Button>
              <Button
                variant={view === "day" ? "contained" : "outlined"}
                startIcon={<TodayIcon />}
                onClick={() => setView("day")}
                sx={compactButtonSx}
              >
                Day
              </Button>
            </Stack>
          </Stack>

          <Stack
            mt={2}
            mb={2}
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "center" }}
          >
            <TextField
              label="Range Start"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Range End"
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          <Tabs
            value={selectedMealPlanId || false}
            onChange={(_, value) => setSelectedMealPlanId(value)}
            sx={{ mb: 2 }}
            variant="scrollable"
          >
            {mealPlans.map((mealPlan) => (
              <Tab
                key={mealPlan.id}
                value={mealPlan.id}
                label={mealPlan.title}
              />
            ))}
          </Tabs>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
            <Card sx={{ borderRadius: "5px", flex: 1 }}>
              <CardContent>
                <Typography variant="overline">Projected Spend</Typography>
                <Typography variant="h5">
                  ${projectedSpend.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ borderRadius: "5px", flex: 1 }}>
              <CardContent>
                <Typography variant="overline">
                  Missing Ingredient Prices
                </Typography>
                <Typography variant="h5">{missingIngredientCount}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ borderRadius: "5px", flex: 1 }}>
              <CardContent>
                <Typography variant="overline">Active View</Typography>
                <Typography variant="h5" sx={{ textTransform: "capitalize" }}>
                  {view}
                </Typography>
              </CardContent>
            </Card>
          </Stack>

          <List sx={{ border: 1, borderColor: "divider", borderRadius: "5px" }}>
            {visibleEntries.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="No scheduled meals in this range"
                  secondary="Use Create Meal Plan to seed a plan, then add meals from the modal."
                />
              </ListItem>
            ) : (
              visibleEntries.map((entry) => (
                <React.Fragment key={entry.id}>
                  <ListItem
                    secondaryAction={
                      <Chip
                        label={formatScheduledMealDate(entry.scheduledAt)}
                        variant="outlined"
                      />
                    }
                  >
                    <ListItemText
                      primary={`${getMealTypeIcon(entry.mealTemplate?.mealType || "dinner")} ${entry.mealTemplate?.name || "Meal"}`}
                      secondary={`${entry.mealTemplate?.description || ""}${
                        entry.notes ? ` • ${entry.notes}` : ""
                      }`}
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))
            )}
          </List>
        </Paper>

        <Stack flex={1} spacing={3}>
          <Paper sx={{ p: 3, borderRadius: "5px" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" fontWeight={700}>
                  Meals Catalog
                </Typography>
                <IconButton
                  color="primary"
                  onClick={() => setOpenMealModal(true)}
                  size="small"
                  sx={{
                    border: 1,
                    borderColor: "primary.main",
                    borderRadius: "5px",
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Stack>
              <TextField
                label="Search Meals"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                size="small"
              />
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              mt={2}
              mb={2}
            >
              <Chip
                label="All"
                clickable
                color={selectedMealType === "all" ? "primary" : "default"}
                onClick={() => setSelectedMealType("all")}
              />
              {mealTypes.map((mealType) => (
                <Chip
                  key={mealType}
                  label={formatMealTypeLabel(mealType)}
                  clickable
                  color={selectedMealType === mealType ? "primary" : "default"}
                  onClick={() => setSelectedMealType(mealType)}
                />
              ))}
            </Stack>
            <Stack spacing={1.5} maxHeight={360} sx={{ overflowY: "auto" }}>
              {filteredMealTemplates.slice(0, 16).map((mealTemplate) => (
                <Paper
                  key={mealTemplate.id}
                  variant="outlined"
                  sx={ledgerItemRowSx}
                >
                  <Box sx={cardContentSx}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {getMealTypeIcon(mealTemplate.mealType)}{" "}
                          {mealTemplate.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {mealTemplate.description}
                        </Typography>
                        {mealTemplate.tags.length ? (
                          <Stack
                            direction="row"
                            spacing={0.75}
                            useFlexGap
                            flexWrap="wrap"
                            mt={1}
                          >
                            {mealTemplate.tags.slice(0, 4).map((tag) => (
                              <Chip
                                key={`${mealTemplate.id}-${tag}`}
                                label={tag}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        ) : null}
                      </Box>
                      <Chip
                        label={formatMealTypeLabel(mealTemplate.mealType)}
                        size="small"
                      />
                    </Stack>
                    <Stack direction="row" justifyContent="flex-end" mt={1}>
                      <IconButton
                        color="primary"
                        onClick={() => addMealTemplateToPlan(mealTemplate)}
                        size="small"
                        sx={{
                          border: 1,
                          borderColor: "primary.main",
                          borderRadius: "5px",
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                </Paper>
              ))}
              {!filteredMealTemplates.length ? (
                <Alert severity="warning" icon={<AutoAwesomeIcon />}>
                  No meal matched. Open Create Meal and use Generate with AI.
                </Alert>
              ) : null}
            </Stack>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: "5px" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={2}
              alignItems={{ sm: "center" }}
            >
              <Typography variant="h6" fontWeight={700}>
                Ingredients Ledger
              </Typography>
              <Chip label={`${ingredients.length} seeded ingredients`} />
            </Stack>
            <Stack
              spacing={1.5}
              mt={2}
              maxHeight={420}
              sx={{ overflowY: "auto" }}
            >
              {(aggregatedIngredients.length
                ? aggregatedIngredients
                : ingredients.slice(0, 24).map((ingredient) => ({
                    name: ingredient.name,
                    total: 1,
                    store: ingredient.defaultStore,
                    price: Number(ingredient.defaultPrice || 0),
                  }))
              ).map((ingredient) => (
                <Paper
                  key={`${ingredient.name}-${ingredient.store}`}
                  variant="outlined"
                  sx={ledgerItemRowSx}
                >
                  <Box sx={cardContentSx}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                      alignItems="center"
                    >
                      <Box>
                        <Typography fontWeight={700}>
                          {ingredient.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Qty {ingredient.total.toFixed(1)} • $
                          {ingredient.price.toFixed(2)} each
                        </Typography>
                      </Box>
                      <Chip
                        icon={<StorefrontIcon />}
                        label={ingredient.store}
                      />
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Stack>
            <Alert severity="info" sx={{ mt: 2 }} icon={<ReceiptLongIcon />}>
              Stores supported: {stores.join(", ")}.
            </Alert>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: "5px" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={2}
              alignItems={{ sm: "center" }}
            >
              <Typography variant="h6" fontWeight={700}>
                Supplies Ledger
              </Typography>
              <Chip label={`${supplies.length} seeded supplies`} />
            </Stack>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Supplies are ready-to-use items like bread, whipped cream, and
              wraps. Ingredients remain raw inputs like flour and milk.
            </Typography>
            <Stack
              spacing={1.5}
              mt={2}
              maxHeight={320}
              sx={{ overflowY: "auto" }}
            >
              {supplies.slice(0, 36).map((supply) => (
                <Paper key={supply.id} variant="outlined" sx={ledgerItemRowSx}>
                  <Box sx={cardContentSx}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                      alignItems="center"
                    >
                      <Box>
                        <Typography fontWeight={700}>{supply.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {supply.defaultUnit} • $
                          {Number(supply.defaultPrice || 0).toFixed(2)}
                        </Typography>
                      </Box>
                      <Chip
                        icon={<StorefrontIcon />}
                        label={supply.defaultStore}
                      />
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </Stack>

      <Dialog
        open={openMealModal}
        onClose={() => setOpenMealModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Meal</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Meal Name"
              value={mealDraft.query}
              onChange={(event) =>
                setMealDraft((current) => ({
                  ...current,
                  query: event.target.value,
                }))
              }
              helperText="Searches the catalog first. If no exact match exists, Generate with AI creates the meal and ingredients."
              fullWidth
            />
            <TextField
              select
              label="Meal Type"
              value={mealDraft.mealType}
              onChange={(event) =>
                setMealDraft((current) => ({
                  ...current,
                  mealType: event.target.value as MealType,
                }))
              }
              fullWidth
            >
              {mealTypes.map((mealType) => (
                <MenuItem key={mealType} value={mealType}>
                  {mealType}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Date & Time"
              type="datetime-local"
              value={mealDraft.scheduledAt}
              onChange={(event) =>
                setMealDraft((current) => ({
                  ...current,
                  scheduledAt: event.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              select
              label="Recurrence"
              value={mealDraft.recurrenceType}
              onChange={(event) =>
                setMealDraft((current) => ({
                  ...current,
                  recurrenceType: event.target.value,
                }))
              }
              fullWidth
            >
              <MenuItem value="none">Does not repeat</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </TextField>
            <TextField
              label="Dietary Hints"
              value={mealDraft.dietaryHints}
              onChange={(event) =>
                setMealDraft((current) => ({
                  ...current,
                  dietaryHints: event.target.value,
                }))
              }
              helperText="Comma-separated. Ex. high protein, gluten free"
              fullWidth
            />
            <TextField
              label="Notes"
              value={mealDraft.notes}
              onChange={(event) =>
                setMealDraft((current) => ({
                  ...current,
                  notes: event.target.value,
                }))
              }
              multiline
              minRows={4}
              fullWidth
            />
            {mealDraft.query &&
            !filteredMealTemplates.some(
              (mealTemplate) =>
                mealTemplate.name.toLowerCase() ===
                mealDraft.query.trim().toLowerCase()
            ) ? (
              <Alert severity="warning" icon={<AutoAwesomeIcon />}>
                No exact meal found. Use Generate with AI. Rate limited to 5
                requests per minute.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMealModal(false)}>Cancel</Button>
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleCreateMeal}
            disabled={!mealDraft.query || generatingMeal}
          >
            {generatingMeal ? "Working..." : "Save / Generate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(statusMessage)}
        autoHideDuration={10000}
        onClose={() => setStatusMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          onClose={() => setStatusMessage("")}
          sx={{ width: "100%" }}
        >
          {statusMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default GroceryMeals;
